import { useEffect, useState } from "react";
import { Nav } from "../components/Nav";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Star, Lock } from "lucide-react";

export default function AdminFeedback() {
  const { user } = useAuth();
  const isAdminUser = !!user?.is_admin;
  const [secret, setSecret] = useState(localStorage.getItem("admin_secret") || "");
  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ count: 0, average: 0 });
  const [loading, setLoading] = useState(false);

  const load = async (s) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/feedback`, s ? { headers: { "X-Admin-Secret": s } } : undefined);
      setItems(data.items || []);
      setStats({ count: data.count || 0, average: data.average || 0 });
      setAuthed(true);
      if (s) localStorage.setItem("admin_secret", s);
    } catch (err) {
      setAuthed(false);
      if (err?.response?.status === 403 && s) alert("Wrong admin secret");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdminUser) load("");
    else if (secret) load(secret);
    /* eslint-disable-next-line */
  }, [isAdminUser]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2]">
        <Nav />
        <div className="max-w-md mx-auto px-6 py-24 fade-up">
          <Lock className="w-8 h-8 text-[#E3C2B0] mb-6" strokeWidth={1} />
          <h1 className="font-serif text-4xl font-light">Admin Access</h1>
          <p className="mt-3 text-sm text-[#A8A6A1]">Enter the admin secret to view feedback.</p>
          <input
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            data-testid="admin-secret"
            placeholder="••••••••"
            className="mt-10 w-full bg-transparent border-b border-white/15 focus:border-[#E3C2B0] outline-none py-3 text-[#F7F5F2] text-lg font-light"
          />
          <button
            onClick={() => load(secret)}
            disabled={loading || !secret}
            data-testid="admin-unlock"
            className="mt-8 w-full bg-[#E3C2B0] text-[#0A0908] py-4 rounded-sm hover:bg-[#F0D4C5] disabled:opacity-50 text-xs uppercase tracking-[0.3em]"
          >
            {loading ? "Checking..." : "Unlock"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2]">
      <Nav />
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
        <div className="fade-up">
          <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">Admin · Feedback</span>
          <h1 className="font-serif text-5xl mt-4 font-light leading-tight">User responses.</h1>
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-6">
          <Stat label="Total feedback" value={stats.count} />
          <Stat label="Average rating" value={`${stats.average.toFixed(1)} / 5`} />
          <Stat label="5-star ratings" value={items.filter((i) => i.rating === 5).length} />
        </div>

        <div className="mt-16 grid gap-px bg-white/5">
          {items.length === 0 && (
            <div className="bg-[#1A1921] p-12 text-center text-[#A8A6A1]">No feedback yet.</div>
          )}
          {items.map((f) => (
            <div key={f.id} className="bg-[#1A1921] p-6 grid md:grid-cols-12 gap-4">
              <div className="md:col-span-2">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className={`w-4 h-4 ${n <= f.rating ? "fill-[#E3C2B0] text-[#E3C2B0]" : "text-[#A8A6A1]/30"}`}
                      strokeWidth={1.5}
                    />
                  ))}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#A8A6A1] mt-3">
                  {new Date(f.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                </div>
              </div>
              <div className="md:col-span-4 text-sm text-[#F7F5F2]/80 font-light">
                <div className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] mb-1">User</div>
                {f.user_email}
              </div>
              <div className="md:col-span-3 text-sm text-[#F7F5F2]/80 font-light">
                <div className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] mb-1">Occasion</div>
                {f.occasion || "—"}
              </div>
              <div className="md:col-span-3 text-sm text-[#F7F5F2]/80 font-light italic leading-relaxed">
                {f.comment || <span className="text-[#A8A6A1]/50">No comment</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-[#1A1921] border border-white/10 p-6">
    <div className="text-xs uppercase tracking-[0.3em] text-[#E3C2B0]">{label}</div>
    <div className="font-serif text-4xl mt-3 font-light">{value}</div>
  </div>
);
